import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { ApiKeyModelData } from "@/store/api-keys/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IDeleteApiKeyOperation } from "@/store/sync/operations/types";
import { SyncModelData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";

export class DeleteApiKeyOperation extends BaseSyncOperation<IDeleteApiKeyOperation> {
  get operationKind(): SyncOperationKind {
    return "DELETE_API_KEY";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const apiKeyUpdate = this.generateDeleteApiKeyUpdate();

    if (apiKeyUpdate) {
      optimisticUpdates.push(apiKeyUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.apiKeys.recompute(this.payload.id);
  }

  protected generateDeleteApiKeyUpdate(): OptimisticSyncUpdate<ApiKeyModelData> | null {
    const observable = this.store.apiKeys.get(this.payload.id);

    if (!observable) {
      logger.debug({
        message: "Attempted to update API key, but it was not found",
        info: { apiKeyId: this.payload.id },
      });

      return null;
    }

    const syncUpdate: OptimisticSyncUpdate<ApiKeyModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "DELETED",
      value: {
        model_id: this.payload.id,
        model_kind: "API_KEY",
        model_version: observable.modelVersion,
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return syncUpdate;
  }
}
